import { ref } from "vue"
import { Modal } from "bootstrap"
import api from "@/api/axios"
import Swal from "sweetalert2"

export function useVueloManual() {

  // =========================
  // FORMAT COP
  // =========================
  const formatoCOP = (valor) => {
    if (valor == null) return "COP 0"

    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(valor)
  }

  // =========================
  // STATE
  // =========================
  const modal = ref(null)
  const loading = ref(false)

  const multiDestino = ref(false)

  const ida = ref({
    aerolinea: "",
    salida: "",
    llegada: "",
    duracion: "",
    tipo: "directo",
    escalas: []
  })

  const intermedio = ref({
    aerolinea: "",
    salida: "",
    llegada: "",
    duracion: "",
    tipo: "directo",
    escalas: []
  })

  const regreso = ref({
    aerolinea: "",
    salida: "",
    llegada: "",
    duracion: "",
    tipo: "directo",
    escalas: []
  })

  const precioSimple = ref("")

  const preciosMulti = ref({
    ida: "",
    intermedio: "",
    regreso: ""
  })

  // =========================
  // HELPERS
  // =========================
  function abrirModal() {
    const el = document.getElementById("vueloManualModal")
    if (!el) return

    modal.value = new Modal(el)
    modal.value.show()
  }

  const limpiarPrecio = (valor) => {
    if (!valor) return 0
    return Number(String(valor).replace(/\D/g, "")) || 0
  }

  // =========================
  // GUARDAR
  // =========================
  async function guardarVuelo(
    searchId,
    fechas,
    vueloManualData = {},

    options = {}
  ) {

    const { sinVuelo = false } = options

    loading.value = true

    try {

      const finalSearchId =
        searchId || `MANUAL-${crypto.randomUUID()}`

      // =====================================================
      // 🔥 BASE DATA (AHORA 100% DEPENDIENTE DE INPUT REAL)
      // =====================================================
      const baseData = {
        searchId: finalSearchId,
        origen: fechas?.origen ?? "",
        destino: fechas?.destino ?? "",
        fechaIda: fechas?.fechaIda,
        fechaRegreso: fechas?.fechaRegreso,
        dias: fechas?.dias,
        diasCombi1: fechas?.diasCombi1,
        diasCombi2: fechas?.diasCombi2,
        habitaciones: fechas?.habitaciones ?? [],
        adultos: fechas?.adultos ?? 0,
        ninos: fechas?.ninos ?? 0,
        infantes: fechas?.infantes ?? 0,
        edadesNinos: fechas?.edadesNinos ?? [],
        edadesInfantes: fechas?.edadesInfantes ?? [],
        telefono: fechas?.telefono ?? "",
        filtrosOpcionales: fechas?.filtrosOpcionales ?? [],
        sinCancelacionGratis: Boolean(fechas?.sinCancelacionGratis)
      }

      const {
        precio,
        ida: idaPayload,
        intermedio: intermedioPayload,
        regreso: regresoPayload
      } = vueloManualData || {}

      // =====================================================
      // 🚫 SIN VUELO (FIX DEFINITIVO)
      // =====================================================
      if (sinVuelo) {

        const payload = {
          ...baseData,
          precio: "COP 0",

          ida: {
            fecha: baseData.fechaIda,
            aerolinea: "no aplica",
            salida: "no aplica",
            llegada: "no aplica",
            duracion: "no aplica",
            tipo: "no aplica",
            escalas: []
          },

          regreso: {
            fecha: baseData.fechaRegreso,
            aerolinea: "no aplica",
            salida: "no aplica",
            llegada: "no aplica",
            duracion: "no aplica",
            tipo: "no aplica",
            escalas: []
          }
        }

        const { data } = await api.post("/guardar-vuelo-manual", payload)

        if (!data.ok) throw new Error(data.error)

        return { ...data, payload }
      }

      // =====================================================
      // ✈️ SIMPLE
      // =====================================================
      const simplePayload = {
        ...baseData,
        precio: formatoCOP(limpiarPrecio(precio)),

        ida: {
          ...(idaPayload || {}),
          fecha: idaPayload?.fecha || baseData.fechaIda,

          escalaCiudad: idaPayload?.escalas?.[0]?.nombre || "",
          duracionEscala: idaPayload?.escalas?.[0]?.duracion || ""
        },

        regreso: {
          ...(regresoPayload || {}),
          fecha: regresoPayload?.fecha || baseData.fechaRegreso,

          escalaCiudad: regresoPayload?.escalas?.[0]?.nombre || "",
          duracionEscala: regresoPayload?.escalas?.[0]?.duracion || ""
        }
      }

      // =====================================================
      // 🌍 MULTI
      // =====================================================
      const multiPayload = {
        ...baseData,
        precio: formatoCOP(limpiarPrecio(precio)),

        resultados: [
          {
            tramo: "ida",
            precioNum: limpiarPrecio(preciosMulti.value.ida),
            ida: {
              ...(idaPayload || {}),
              fecha: baseData.fechaIda,
              escalaCiudad: idaPayload?.escalas?.[0]?.nombre || "",
              duracionEscala: idaPayload?.escalas?.[0]?.duracion || ""
            }
          },
          {
            tramo: "intermedio",
            precioNum: limpiarPrecio(preciosMulti.value.intermedio),
            ida: {
              ...(intermedioPayload || {}),
              escalaCiudad: intermedioPayload?.escalas?.[0]?.nombre || "",
              duracionEscala: intermedioPayload?.escalas?.[0]?.duracion || ""
            }
          },
          {
            tramo: "regreso",
            precioNum: limpiarPrecio(preciosMulti.value.regreso),
            ida: {
              ...(regresoPayload || {}),
              fecha: baseData.fechaRegreso,
              escalaCiudad: regresoPayload?.escalas?.[0]?.nombre || "",
              duracionEscala: regresoPayload?.escalas?.[0]?.duracion || ""
            }
          }
        ]
      }

      const payload = !multiDestino.value
        ? simplePayload
        : multiPayload

      const { data } = await api.post("/guardar-vuelo-manual", payload)

      if (!data.ok) throw new Error(data.error)



      return {
        ...data,
        payload
      }

    } catch (err) {

      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.error || err.message
      })

    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    multiDestino,

    ida,
    intermedio,
    regreso,

    precioSimple,
    preciosMulti,

    abrirModal,
    guardarVuelo
  }
}